import { makeAutoObservable, runInAction } from "mobx";
import { ICalculatorFeeDto, ICalculatorItemBalanceDto, ICalculatorRequestDTO, ICalculatorResponseDTO, ICalculatorShippingCostDto, IIbanDetailsDto, IPricesBreakdownDto, IRefundRequestDto, REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS, REFUND_METHODS } from "../components/refund-calculator-drawer/refund-calculator-types";
import RefundCalculatorService from "../../../../services/api/refund-calculator-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import RefundCalculatorCategoryStore, { ILinesSubtotal } from "./refund-calculator-category-store";
import { IOption } from "../../../../components/inputs/select-dropdown/select-dropdown";
import ProductCaseStore from "./product-case-store";
import currentPortalStore from "../../../../stores/current-portal-store";
import { RESTOCK_BY } from "../../../../models/User";
import CustomerCaseStore from "./customer-case-store";
import { IMoneyDto } from "../rma-types";
import { IInputValitationResponse } from "../../../../hooks/input-validation-hook";
import RmaPageStore from "./rma-page-store";

interface IMoneyDtoFormatted extends IMoneyDto {
    formatted_amount?: string;
}

export interface IRefundOption extends IOption {
    refund_type: REFUND_METHODS;
}

class RefundCalculatorStore {
    private readonly _virtualCreditRefundMethods: REFUND_METHODS[] = [
        REFUND_METHODS.GIFT_CARD,
        REFUND_METHODS.STORE_CREDITS,
        REFUND_METHODS.PLATFORM_STORE_CREDITS,
    ];

    private _isLoading: boolean = false;
    private _refundCalculatorCategories: RefundCalculatorCategoryStore[] = [];
    private _calculatorRequestDTO?: ICalculatorRequestDTO;
    private _calculatorResponseDto?: ICalculatorResponseDTO;
    private _actualRefundAmount?: IMoneyDtoFormatted;
    private _changes: ICalculatorResponseDTO = {} as ICalculatorResponseDTO;
    private _selectedProductCases: ProductCaseStore[] = [];
    private _customerCaseStore?: CustomerCaseStore;
    /** Sysadmin-only: filled after Reveal (RGET-3113). Merchants use calculator response iban_details. */
    private _revealedIbanDetails?: IIbanDetailsDto;
    private _isLoadingIbanDetails: boolean = false;
    private _ibanDetailsLoadFailed: boolean = false;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    private get _service(): RefundCalculatorService { return rootContainer.get<RefundCalculatorService>(TYPE.RefundCalculatorService);}
    public get refundCalculatorCategories(): RefundCalculatorCategoryStore[] { return this._refundCalculatorCategories; }
    public get paymentCurrency(): string { return this._calculatorResponseDto ? this._calculatorResponseDto.payment_currency : ""; }
    public get shopCurrency(): string { return this._calculatorResponseDto ? this._calculatorResponseDto.shop_currency : ""; }
    public get conversionRate(): number { return this._calculatorResponseDto ? this._calculatorResponseDto.conversion_rate : 1; }

    /**
     * selectedRefundMethodId and selectedRefundMethodType work together, they can be not equal in cases if
     * refund type is based on integration (for example Rise).
     */
    public get selectedRefundMethodId(): number { return this._changes.selected_refund_method_id ?? this._calculatorResponseDto?.selected_refund_method_id; }
    public get selectedRefundMethodType(): REFUND_METHODS { return this._changes.selected_refund_method_type ?? this._calculatorResponseDto?.selected_refund_method_type; }

    public get IBANDetails(): IIbanDetailsDto | undefined {
        return this._revealedIbanDetails ?? this._calculatorResponseDto?.iban_details ?? undefined;
    }
    public get isLoadingIbanDetails(): boolean { return this._isLoadingIbanDetails; }
    public get ibanDetailsLoadFailed(): boolean { return this._ibanDetailsLoadFailed; }
    /** True when calculator omitted IBAN (sysadmin path) and reveal has not succeeded yet. */
    public get needsIbanReveal(): boolean {
        return !this.IBANDetails;
    }

    public get showTax():boolean {
        const selectedRefundMethod = this._calculatorResponseDto?.refund_method_options.find(option => option.id === this.selectedRefundMethodType);
        return (selectedRefundMethod?.show_taxes ?? true) && !this._calculatorResponseDto?.is_tax_included;
    }
    public get restock(): boolean { return this._changes.restock?.checked ?? this._calculatorResponseDto?.restock.checked;}
    public get notifyOnPlatform(): boolean { return this._changes.notify_on_platform ?? this._calculatorResponseDto?.notify_on_platform; }
    public get restockLocationId(): string | undefined { return this._changes.restock_location_id ?? this._calculatorResponseDto?.restock_location_id; }
    public get itemsPaidAmountInStoreCurrency(): IMoneyDto | undefined {
        return this._calculatorResponseDto?.items_paid_amount?.find( item => item.currency_code === this._calculatorResponseDto?.payment_currency );
    }
    public get isItemsLevelRefund(): boolean { return this._selectedProductCases.length > 0; }
    public get rmaId(): number | undefined { return this._calculatorRequestDTO?.rma_id; }

    public get isOriginalPaymentCaptured(): boolean {
        return !!this._calculatorResponseDto?.is_original_payment_captured;
    }

    private _buildCalculatorRequest(rmaId: number, selectedProductCases: ProductCaseStore[]): ICalculatorRequestDTO {
        const productCaseIdsList = selectedProductCases.map(selectedProductCase => {
            return { product_case_id: selectedProductCase.id }
        });
        return {rma_id: rmaId, items: productCaseIdsList};
    }

    private _clearCalculatorStore() {
        this._selectedProductCases = [];
        this._calculatorResponseDto = undefined;
        this._calculatorRequestDTO = undefined;
        this._refundCalculatorCategories = [];
        this._actualRefundAmount = undefined;
        this._revealedIbanDetails = undefined;
        this._isLoadingIbanDetails = false;
        this._ibanDetailsLoadFailed = false;
    }

    public async revealIbanDetails(): Promise<void> {
        if (!this.rmaId || this._isLoadingIbanDetails || this._revealedIbanDetails) {
            return;
        }
        this._isLoadingIbanDetails = true;
        this._ibanDetailsLoadFailed = false;
        try {
            const details = await this._service.loadIbanDetails(this.rmaId);
            runInAction(() => {
                this._revealedIbanDetails = details;
                this._isLoadingIbanDetails = false;
            });
        } catch {
            runInAction(() => {
                this._ibanDetailsLoadFailed = true;
                this._isLoadingIbanDetails = false;
            });
        }
    }

    public async load(rmaId: number, selectedProductCases: ProductCaseStore[], customerCaseStore: CustomerCaseStore | undefined): Promise<void> {
        this._isLoading = true;
        this._clearCalculatorStore();
        try {
            const dto = this._buildCalculatorRequest(rmaId, selectedProductCases);
            this._selectedProductCases = selectedProductCases;
            const res = await this._service.fetchCalculatorData(dto, rmaId);
            runInAction(() => {
                this._isLoading = false;
                this._calculatorResponseDto = res;
                this._calculatorRequestDTO = dto;
                this._customerCaseStore = customerCaseStore;
                const balanceStore = new RefundCalculatorCategoryStore(
                    res.prices_breakdown.balance as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.BALANCE,
                    res.payment_currency,
                    this._getProductName,
                );
                /**
                 * The API always returns the outbound shipping row and flags it is_hidden when
                 * store/policy settings say the shipping cost is not refundable, so drop those
                 * rows and skip the category when nothing is left to show.
                 */
                const visibleShippingCost = res.prices_breakdown.shipping_cost.filter(line => !line.is_hidden);
                const shippingStore = visibleShippingCost.length ? new RefundCalculatorCategoryStore(
                    visibleShippingCost as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_SHIPPING_COST,
                    res.payment_currency
                ) : null;
                const restockingFeeStore = res.prices_breakdown.paid_restocking_fees ? new RefundCalculatorCategoryStore(
                    res.prices_breakdown.paid_restocking_fees as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_RESTOCKING_FEE,
                    res.payment_currency
                ) : null;
                const rmaFeeStore = res.prices_breakdown.paid_rma_fees ? new RefundCalculatorCategoryStore(
                    res.prices_breakdown.paid_rma_fees as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_RETURN_FEE,
                    res.payment_currency
                ) : null;
                const returnLabelFeeStore = res.prices_breakdown.paid_return_label_fees ? new RefundCalculatorCategoryStore(
                    res.prices_breakdown.paid_return_label_fees as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_RETURN_LABEL_FEE,
                    res.payment_currency
                ) : null;
                const cancellationFeeStore = res.prices_breakdown.paid_cancellation_fees ? new RefundCalculatorCategoryStore(
                    res.prices_breakdown.paid_cancellation_fees as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_CANCELLATION_FEE,
                    res.payment_currency
                ) : null;
                const returnLabelFeeDeductionStore = res.prices_breakdown.return_label_fee_deduction ? new RefundCalculatorCategoryStore(
                    /* wrap in array because the store expects an array of lines and it's coming as a single item from the API */
                    [res.prices_breakdown.return_label_fee_deduction] as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.RETURN_LABEL_FEE_DEDUCTION,
                    res.payment_currency,
                    undefined,
                    true
                ) : null;
                const rmaFeeDeductionStore = res.prices_breakdown.rma_fee_deduction ? new RefundCalculatorCategoryStore(
                    /* wrap in array because the store expects an array of lines and it's coming as a single item from the API */
                    [res.prices_breakdown.rma_fee_deduction] as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.RETURN_FEE_DEDUCTION,
                    res.payment_currency,
                    undefined,
                    true
                ) : null;
                const cancellationFeeDeductionStore = res.prices_breakdown.cancellation_fee_deduction ? new RefundCalculatorCategoryStore(
                    /* wrap in array because the store expects an array of lines and it's coming as a single item from the API */
                    [res.prices_breakdown.cancellation_fee_deduction] as (ICalculatorItemBalanceDto & ICalculatorShippingCostDto & ICalculatorFeeDto)[],
                    REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.CANCELLATION_FEE_DEDUCTION,
                    res.payment_currency,
                    undefined,
                    true
                ) : null;

                const refundCalculatorCategories = [balanceStore, rmaFeeDeductionStore, returnLabelFeeDeductionStore, cancellationFeeDeductionStore, shippingStore, restockingFeeStore, rmaFeeStore, returnLabelFeeStore, cancellationFeeStore];

                /** filter out the optional categories (fee deductions) that are not present in the response from the API */
                this._refundCalculatorCategories = refundCalculatorCategories.filter(category => !!category) as RefundCalculatorCategoryStore[];

                if (res.auto_populate_refund_amount) {
                    let amount = this.getSuggestedRefund();
                    if (amount < 0) {
                        amount = 0;
                    }
                    this.setActualRefundAmount(amount, true);
                }
            })
        } catch (e) {
            runInAction(() => {
                this._isLoading = false;
            })
            console.log(e);
        }
    }

    /**
     * Duties follow the RMA setting (`is_hidden` from API), not the selected refund method.
     * Kept as a getter so the grid can gate the duties row in one place.
     */
    public get areDutiesRefundable(): boolean {
        return true;
    }

    public getSubtotal(): ILinesSubtotal {
        const categoriesSubtotalsList = this._refundCalculatorCategories.map(category =>
            category.getLinesSubtotal()
        );
        if (this.areDutiesRefundable) {
            categoriesSubtotalsList.push(...this._refundCalculatorCategories.map(category => category.getDutiesSubtotal()));
        }

        const priceSubtotal = categoriesSubtotalsList.reduce((acc, subtotal) =>
            acc + subtotal.price, 0
        );

        const taxSubtotal = categoriesSubtotalsList.reduce((acc, subtotal) =>
            acc + subtotal.tax, 0
        );

        return { price: priceSubtotal, tax: taxSubtotal };
    }

    public getSuggestedRefund(): number {
        const subtotal = this.getSubtotal();
        return this.showTax ? Number((subtotal.price + subtotal.tax).toFixed(2)) : Number((subtotal.price).toFixed(2));
    }

    public getSuggestedRefundInShopCurrency(): number {
        const suggestedRefund = this.getSuggestedRefund();
        return suggestedRefund * this.conversionRate;
    }

    public shouldShowShopCurrency(): boolean {
        return this._calculatorResponseDto?.payment_currency !== this._calculatorResponseDto?.shop_currency;
    }

    public getActualRefundAmount(): number|string|undefined {
        return this._actualRefundAmount?.formatted_amount ?? this._actualRefundAmount?.amount;
    }

    /** the amount is formatted in 2 scenarios, when we click on apply and when the input is auto-populated */
    public setActualRefundAmount(amount: number, shouldFormat: boolean = false): void {
        this._actualRefundAmount = {
            amount: amount,
            currency_code: this._calculatorResponseDto?.payment_currency as string,
            formatted_amount: shouldFormat ? amount.toFixed(2) : undefined,
        };
    }

    public validateRefundAmount(): IInputValitationResponse {
        let isValid: boolean = true;
        let messages: string[] = [];

        if (!this._actualRefundAmount?.amount ||
            isNaN(Number(this._actualRefundAmount.amount)) ||
                this._actualRefundAmount.amount <= 0 ||
                    (this.selectedRefundMethodType !== REFUND_METHODS.BANK_REFUND && this._actualRefundAmount.amount > this.getAvailableAmount())
           ) {
               isValid = false;
               messages.push('Invalid amount');
           }

           return {
               isValid: isValid,
               messages: messages,
           }
    }

    public getRefundMethodOptions(): IOption[] {
        if (!this._calculatorResponseDto) {
            return [{} as IOption];
        }
        return this._calculatorResponseDto.refund_method_options.map((option) => {
            return {
                label: option.label,
                value: option.id,
                refund_type: option.refund_type,
            }
        });
    }

    public setSelectedRefundOption(refundOption: IRefundOption) {
        if (!this._calculatorResponseDto) {
            console.error('Should not happen, if select event happened - options should have been loaded');
            return;
        }

        this._changes.selected_refund_method_id = refundOption.value as number;
        this._changes.selected_refund_method_type = refundOption.refund_type;

        if (this._virtualCreditRefundMethods.includes(refundOption.refund_type)) {
            this.setNotifyOnPlatform(true);
        }
    }

    public getAvailableAmount(): number {
        /** for bank refund we don't show available amount and don't limit the refund, so this 0 won't be visible */
        if (this.selectedRefundMethodType === REFUND_METHODS.BANK_REFUND) {
            return 0;
        }
        /** for refund to original payment and platform store credit, the cap is net payment (order refund path) */
        if (
            this.selectedRefundMethodType === REFUND_METHODS.PAYMENT_METHOD
            || this.selectedRefundMethodType === REFUND_METHODS.PLATFORM_STORE_CREDITS
        ) {
            return this._calculatorResponseDto?.net_payment.find(amount => amount.currency_code === this.paymentCurrency)?.amount ?? 0;
        }
        /** for virtual refund methods (gift card, credits etc) the available amount changes according to selections on the refund calculator */
        const categoriesAvailableAmountSubtotals = this._refundCalculatorCategories.map( category => category.getAvailableAmountSubtotal(this.selectedRefundMethodType));
        let availableAmount = categoriesAvailableAmountSubtotals.reduce((acc, subtotal) => acc + subtotal, 0) ?? 0;

        // Duties have no available_virtual_amount; add the selected duties the same way as suggested refund
        if (this.areDutiesRefundable) {
            const dutiesSubtotal = this._refundCalculatorCategories
                .map(category => category.getDutiesSubtotal())
                .reduce((acc, subtotal) => ({ price: acc.price + subtotal.price, tax: acc.tax + subtotal.tax }), { price: 0, tax: 0 });
            availableAmount += this.showTax ? dutiesSubtotal.price + dutiesSubtotal.tax : dutiesSubtotal.price;
        }

        return availableAmount;
    }

    public setRestock(v: boolean): void {
        this._changes.restock = {checked: v, disabled: false};
    }

    public setNotifyOnPlatform(v: boolean): void {
        this._changes.notify_on_platform = v;
    }

    public getRestockingLocationsList(): IOption[] {
        if (!this._calculatorResponseDto) {
            return [{} as IOption];
        }

        return this._calculatorResponseDto?.restocking_location_options.map(option =>
            {
                return {
                    label: option.label,
                    value: option.id
                }
            }
        );
    }

    public setRestockLocationId(id: string): void {
        this._changes.restock_location_id = id;
    }

    private _getUpdatedCategory(categoryTitle: string) {
        const category = this._refundCalculatorCategories.find(category => category.title === categoryTitle);
        if (!category) {
            return [];
        }
        return category.getUpdatedlines();
    }

    /** For fee deductions */
    private _getUpdatedSingleLineCategory(categoryTitle: string) {
        const updatedCategory = this._getUpdatedCategory(categoryTitle);
        return updatedCategory.length > 0 ? {...updatedCategory[0]} : null;
    }

    public buildRefundRequestDto(): IRefundRequestDto|undefined {
        if (!this._actualRefundAmount) {
            console.error('Missing refund amount');
            return undefined;
        }

        const pricesBreakdown = {
            paid_restocking_fees: this._getUpdatedCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_RESTOCKING_FEE) as unknown as ICalculatorFeeDto[],
            balance: this._getUpdatedCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.BALANCE) as unknown as ICalculatorItemBalanceDto[],
            shipping_cost: this._getUpdatedCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_SHIPPING_COST) as unknown as ICalculatorShippingCostDto[],
            paid_return_label_fees: this._getUpdatedCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_RETURN_LABEL_FEE) as unknown as ICalculatorFeeDto[],
            paid_rma_fees: this._getUpdatedCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_RETURN_FEE) as unknown as ICalculatorFeeDto[],
            paid_cancellation_fees: this._getUpdatedCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.PAID_CANCELLATION_FEE) as unknown as ICalculatorFeeDto[],
            return_label_fee_deduction: this._getUpdatedSingleLineCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.RETURN_LABEL_FEE_DEDUCTION) as unknown as ICalculatorFeeDto,
            rma_fee_deduction: this._getUpdatedSingleLineCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.RETURN_FEE_DEDUCTION) as unknown as ICalculatorFeeDto,
            cancellation_fee_deduction: this._getUpdatedSingleLineCategory(REFUND_CALCULATOR_CATEGORY_TRANSLATION_KEYS.CANCELLATION_FEE_DEDUCTION) as unknown as ICalculatorFeeDto,
        }

        /** Flter out categories that were not received from the API */
        const filteredPricesBreakdown = Object.fromEntries(
            Object.entries(pricesBreakdown).filter(([_, value]) => value !== null)
        );

        return {
            ...this._calculatorResponseDto,
            ...this._calculatorRequestDTO as ICalculatorRequestDTO,
            ...this._changes,
            prices_breakdown: filteredPricesBreakdown as unknown as IPricesBreakdownDto,
            refund_amount: this._actualRefundAmount,
            iban_details: this.IBANDetails,
        }
    }

    public isSendNotificationToggleDisabled(): boolean {
        if (this._virtualCreditRefundMethods.includes(this.selectedRefundMethodType)) {
            return true;
        }
        return false;
    }

    public getEmailToggleText(): string {
        if (this.isSendNotificationToggleDisabled()) {
            return "SEND_EMAIL_NOTIFICATIONS";
        }
        return "SEND_PLATFORM_NOTIFICATIONS"
    }

    public showIBANSection(): boolean {
        if (this.selectedRefundMethodType === REFUND_METHODS.BANK_REFUND) {
            return true;
        }
        return false;
    }

    /** When refund to paymeny method is selected, we should show info note if some of the items were refunded or has exchange resolution*/
    public getRestrictionsNoteText(): string | undefined {
        if (this.selectedRefundMethodType !== REFUND_METHODS.PAYMENT_METHOD) {
            return undefined;
        }
        const refundedItemsCount = this._selectedProductCases.filter(item => item.isSuccessfullyRefunded).length;
        const itemsWithExchangeResolutionCount = this._selectedProductCases.filter(item => item.exchangeStore).length;
        if (refundedItemsCount > 0 && itemsWithExchangeResolutionCount > 0) {
            return (`${refundedItemsCount} of the selected items were already refunded and therefore can't be refunded. The resolution for ${itemsWithExchangeResolutionCount} of the selected items is Exchange.`);
        }

        if (refundedItemsCount > 0) {
            return `${refundedItemsCount} of the selected items were already refunded and therefore can't be refunded.`;
        }

        if (itemsWithExchangeResolutionCount > 0) {
            return `The resolution for ${itemsWithExchangeResolutionCount} of the selected items is Exchange.`;
        }

        return undefined;
    }

    public showRestockWarningNote(rma: RmaPageStore): boolean {
        return (
			currentPortalStore.setting?.restockBy === RESTOCK_BY.RESTOCK_BY_REFUND_PROCESS &&
            this.selectedRefundMethodType === REFUND_METHODS.PAYMENT_METHOD &&
	        !this.restock &&
			!rma.isRestockDisabled()
        );
    }

    public getGiftCardOrCreditsNoteText(): string | undefined{
        if (!this._virtualCreditRefundMethods.includes(this.selectedRefundMethodType)) {
            return undefined;
        }
        if (this.selectedRefundMethodType === REFUND_METHODS.GIFT_CARD) {
            return "GIFT_CARD_NOTE_TEXT";
        }

        return "CREDITS_NOTE_TEXT";
    }

    /** We take the product case from customer case store because if it's an order-level refund, we have no selected product cases list to search in */
    private _getProductName(productCaseId: number): string {
        if (!this._customerCaseStore) {
            return `${productCaseId}`;
        }
        const productCase = this._customerCaseStore.productCaseList.find(productCase => productCase.id === productCaseId);
        if (!productCase) {
            return `${productCaseId}`;
        }
        return productCase.productStore.name;
    }

    public shouldShowAvailableAmount(): boolean {
        return this.selectedRefundMethodType !== REFUND_METHODS.BANK_REFUND;
    }

    private canRefundAlreadyRefundedItems(): boolean {
        return [
            REFUND_METHODS.STORE_CREDITS,
            REFUND_METHODS.GIFT_CARD,
            REFUND_METHODS.BANK_REFUND
        ].includes(this.selectedRefundMethodType);
    }

    public isRefundValid(): boolean {
        if (this._selectedProductCases.length === 0) {
            return true;
        }

        if (this.canRefundAlreadyRefundedItems()) {
            return true;
        }

        return !this._selectedProductCases.every(item => item.isSuccessfullyRefunded);
    }
}

export default RefundCalculatorStore;
